import DashboardLayout from '@/components/layout/DashboardLayout';
import ProfileLayout from '@/components/layout/ProfileLayout';
import { useAuth } from '@/context/AuthContext';
import { HeaderStateProvider } from '@/context/HeaderStateContext';
import { HeaderStateProvider2 } from '@/context/HeaderStateContext2';
import { HeaderStateProvider3 } from '@/context/HeaderStateContext3';
import { HeaderAdsProvider } from '@/context/HeaderAdsContext';
import { useOnboardingStatus } from '@/lib/api/queries/useOnboarding';
import { OnboardingProvider } from '@/context/OnboardingContext.tsx';
import { PlanAuthorizationProvider } from '@/context/PlanAuthorizationContext';
import { Spin } from 'antd';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  matchPath,
} from 'react-router-dom';
import { CampaignIdsProvider } from '@/context/CampaignIdsContext';
import { TableFilterProvider } from '@/context/TableFilterContext';
import { GrowthTrackingFiltersProvider } from '@/context/GrowthTrackingFiltersContext';
import { InsightFiltersProvider } from '@/context/InsightFiltersContext';
import { useUserDetails } from '@/hooks/useUserDetails';
import { DateRangeProvider } from '@/context/DateRangeContext';
import { DropdownsProvider } from '@/context/DropdownsContext';
import { RegionProvider } from '@/context/CountryContext';
import { Suspense, lazy } from 'react';
import FeedbackButton from '@/components/reuseable/Feedback.tsx';
import { useGetActiveSubscription } from '@/lib/api/hooks/useCreateSubscription';
// Lazy load components to reduce initial bundle size
const SnapshotPage = lazy(() => import('@/pages/amazon/snapshot'));
const AuthCallback = lazy(() => import('@/pages/auth/AuthCallback'));
const RegisterForm = lazy(() =>
  import('@/pages/auth/register/RegisterForm').then((module) => ({
    default: module.RegisterForm,
  }))
);
const VerifyEmail = lazy(() => import('@/pages/auth/verify-email/VerifyEmail'));
const VerificationError = lazy(
  () => import('@/pages/auth/verify-email/VerificationError')
);
const ResendVerification = lazy(
  () => import('@/pages/auth/verify-email/ResendVerification')
);
const ForgotPassword = lazy(
  () => import('@/pages/auth/forgot-password/ForgotPassword')
);
const ResetPassword = lazy(
  () => import('@/pages/auth/reset-password/ResetPassword')
);
const Onboarding = lazy(() => import('@/pages/onboarding/Onboarding'));
const ProfileEditPage = lazy(() => import('@/pages/profile/ProfileEditPage'));
const ProfileOverviewPage = lazy(
  () => import('@/pages/profile/ProfileOverviewPage')
);
const ProfilePasswordChangePage = lazy(
  () => import('@/pages/profile/ProfilePasswordChangePage')
);
const Unauthorized = lazy(() =>
  import('@/pages/Unauthorized').then((module) => ({
    default: module.Unauthorized,
  }))
);
const AutomatedRulesPage = lazy(() => import('@/pages/amazon/automated-rules'));
const Dsp = lazy(() => import('@/pages/amazon/dsp'));
const BlacklistKeyword = lazy(
  () => import('@/pages/amazon/blacklist-keywords')
);
const DaypartingPage = lazy(() => import('@/pages/amazon/day-parting'));
const DayPartingLayout = lazy(
  () => import('@/components/layout/DayPartingLayout')
);
const SchedulerPage = lazy(
  () => import('@/pages/amazon/day-parting/scheduler')
);
const HistoryPage = lazy(() => import('@/pages/amazon/day-parting/history'));
const SchedulerLogPage = lazy(() => import('@/pages/amazon/day-parting/log'));
const MasterOverviewPage = lazy(() => import('@/pages/amazon/masterOverview'));
const CustomTableComponent = lazy(
  () => import('@/pages/amazon/text-categorization')
);
const AudienceInstancePage = lazy(
  () => import('@/pages/amazon/amc/audienceInstanceTable')
);
const CreateAudiencePage = lazy(
  () => import('@/pages/amazon/amc/CreateAudiencePage')
);
const AudienceReportPage = lazy(
  () => import('@/pages/amazon/amc/AudienceReportPage')
);
const AmazonInsights = lazy(
  () => import('@/pages/amazon/amc/AmazonInsightsPage')
);
const CampaignsPage = lazy(() => import('@/pages/amazon/target'));
const AccountsPage = lazy(() => import('@/pages/amazon/accounts'));
const UserManagement = lazy(() => import('@/pages/user-management'));
const AcceptInvite = lazy(
  () => import('@/pages/auth/invitation/accept-invite')
);
const SellcoAI = lazy(() => import('@/pages/amazon/sellco-ai'));
const InvoicesPage = lazy(() => import('@/components/profile/Invoices'));
const BillingPage = lazy(() => import('@/pages/profile/BillingPage'));
const InsightPage = lazy(() => import('@/pages/amazon/insight'));
const AsinManagement = lazy(() => import('@/pages/asin-management'));
const GrowthTracking = lazy(() => import('@/pages/amazon/growth-tracking'));
const Insights = lazy(() => import('@/components/amazon/ads/insights'));
const Automation = lazy(() => import('@/components/amazon/ads/automation'));
const Dayparting = lazy(() => import('@/components/amazon/ads/daypartying'));
const CampaignManager = lazy(
  () => import('@/components/amazon/ads/campaignManager')
);
const AdGroups = lazy(
  () => import('@/components/amazon/ads/campaignManager/AdGroups')
);
const MarketingCloud = lazy(
  () => import('@/components/amazon/ads/marketingCloud')
);
const Stream = lazy(() => import('@/components/amazon/ads/stream'));
const History = lazy(() => import('@/components/amazon/ads/history'));
const TargetAndSearchTerms = lazy(
  () => import('@/components/amazon/automated-rules/ads/TargetsAndSearchTerms/TargetAndSearchTerms')
);
import PortfolioPage from '@/components/amazon/ads/campaigns/PortfolioPage';
const retryImport = <T,>(importer: () => Promise<T>, key: string): Promise<T> =>
  importer().catch((error) => {
    const storageKey = `sellco-retry:${key}`;
    if (!sessionStorage.getItem(storageKey)) {
      sessionStorage.setItem(storageKey, '1');
      window.location.reload();
      return new Promise(() => {});
    }
    sessionStorage.removeItem(storageKey);
    throw error;
  });
const AdsPage = lazy(() => retryImport(() => import('@/pages/amazon/ads'), 'ads-page'));
const Overview = lazy(() =>
  retryImport(() => import('@/components/amazon/ads/overview'), 'ads-overview')
);
const ProfilesPage = lazy(() => import('@/pages/amazon/profiles'));
const LoginForm = lazy(() => import('@/pages/auth/login/LoginForm'));
const OtpVerification = lazy(() => import('@/components/otpVerification'));
const NoAccess = lazy(() => import('@/components/NoAccess'));
const TrialExpiredPage = lazy(() => import('@/pages/profile/TrialExpiredPage'));
const AdRevenueLimitPage = lazy(() => import('@/pages/profile/AdRevenueLimitPage'));
const PaymentCompletePage = lazy(() => import('@/pages/profile/PaymentCompletePage'));
const SuspendedPage = lazy(() => import('@/pages/profile/SuspendedPage'));

// Legal policy pages
const TermsAndServices = lazy(() => import('@/pages/profile/TermsAndServices'));
const RefundPolicy = lazy(() => import('@/pages/profile/RefundPolicy'));
const PrivacyPolicy = lazy(() => import('@/pages/profile/PrivacyPolicy'));
const NotificationsPage = lazy(() => import('@/pages/profile/NotificationsPage'));
// Protected route wrapper component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const userId = user?.sub ?? 0;
  const { userDetails, isLoading: userDetailsLoading } = useUserDetails();
  // call the onboarding query using the actual user id.
  const {
    data: onboardingData,
    isLoading: onboardingLoading,
    error: onboardingError,
  } = useOnboardingStatus(userId);

  // Show a spinner while auth or user details are loading.
  if (authLoading || userDetailsLoading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  // If no user is present (e.g. token expired), redirect to login.
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user was invited/created by an owner, skip onboarding redirect
  if (userDetails?.createdById) {
    return <>{children}</>;
  }

  if (onboardingLoading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  // If there's an error with onboarding data, or onboarding is incomplete, redirect appropriately.
  // A 402 means the account is past its plan: let it through so the route
  // guard can show the upgrade page; sending it to /login looped forever.
  if (onboardingError && (onboardingError as any)?.response?.status !== 402) {
    return <Navigate to="/login" replace />;
  }

  if (onboardingData && !onboardingData.completed) {
    return <Navigate to="/onboarding" replace />;
  }

  // If everything is in order, render the protected content.
  return (
    <>
      {children}
      <FeedbackButton />
    </>
  );
};

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center p-8">
    <Spin size="large" />
  </div>
);

// Authorization Route
export const AuthorizedRoute = ({ children }: { children: React.ReactNode }) => {
  const { data: activeSubscription, isLoading } = useGetActiveSubscription();
  const location = useLocation();
  const preDefinedRoutes = [
    '/dashboard',
    '/dashboard/insight-360',
    '/dashboard/growth-tracking',
    '/dashboard/legal/*',
    '/dashboard/profile/*',
    '/dashboard/profile/privacy-policy',
    '/dashboard/profile/billing',
    '/dashboard/profile/terms-and-services',
    '/dashboard/profile/refund-and-cancellation',
    '/dashboard/accounts',
    '/dashboard/user-management',
    '/dashboard/profiles',
  ];
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!activeSubscription) {
    return <Navigate to="/no-access" replace />;
  }

  // The endpoint reports "no subscription" inside a 200 with no data. That is
  // an account with nothing to use, so it belongs on the upgrade page rather
  // than on the routes every plan includes.
  if (!activeSubscription?.data) {
    return <Navigate to="/trial-expired" replace />;
  }

  // Access state enforcement (FE-030 / CR-042)
  const access = activeSubscription?.data?.access;
  const accessState = access?.state;

  if (accessState === 'SUSPENDED') {
    return <Navigate to="/suspended" replace />;
  }

  if (accessState === 'RESTRICTED' || accessState === 'EXPIRED') {
    const lockdownAllowedRoutes = [
      '/dashboard/profile',
      '/dashboard/legal',
      '/dashboard/accounts',
      '/dashboard/user-management',
      '/dashboard/profiles',
    ];
    const isLockdownAllowed = lockdownAllowedRoutes.some((route) =>
      matchPath({ path: route, end: false }, location.pathname)
    );

    if (!isLockdownAllowed) {
      return <Navigate to="/dashboard/profile/billing" replace />;
    }
  }

  // If the backend signals trial has expired, send user to the upgrade wall.
  // All API calls are implicitly stopped because they only live inside authorized routes.
  if (activeSubscription?.data?.trialExpired || activeSubscription?.data?.plan?.planName === 'Basic') {
    return <Navigate to="/trial-expired" replace />;
  }

  // Scale customers over the ads-revenue threshold must switch plan before
  // carrying on. Custom customers pay commission instead, so they are never sent here.
  const adRevenue = activeSubscription?.data?.adRevenue;
  if (
    adRevenue?.state === 'exceeded' &&
    adRevenue?.planTier === 'scale' &&
    !matchPath({ path: '/dashboard/profile', end: false }, location.pathname)
  ) {
    return <Navigate to="/ad-revenue-limit" replace />;
  }

  const allowedRoutes: string[] = [
    ...preDefinedRoutes,
    ...(activeSubscription?.data?.plan?.planFeatures?.map((f) => f.route) ||
      []),
  ];

  // A granted route covers its detail pages too: several features are routed as
  // `<route>/:id`, which an exact match would deny even though the plan allows
  // the feature. `end: false` matches on segment boundaries, so `/sellco-ai`
  // grants `/sellco-ai/123` without also granting `/sellco-ai-admin`.
  const isAllowed = allowedRoutes?.some((route) => {
    const baseRoute = route.endsWith('/*') ? route.replace('/*', '') : route;
    return matchPath({ path: baseRoute, end: baseRoute === '/dashboard' }, location.pathname);
  });

  if (!isAllowed) {
    return <Navigate to="/no-access" replace />;
  }

  return (
    <>
      <PlanAuthorizationProvider
        authorization={activeSubscription?.data?.plan?.authorization}
      >
        {children}
      </PlanAuthorizationProvider>
    </>
  );
};

// Public route wrapper component
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <>
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginForm />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterForm />
                </PublicRoute>
              }
            />
            <Route
              path="/otp-verification"
              element={
                <PublicRoute>
                  <OtpVerification />
                </PublicRoute>
              }
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verification-error" element={<VerificationError />} />
            <Route
              path="/resend-verification"
              element={<ResendVerification />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route
              path="/onboarding"
              element={
                <OnboardingProvider>
                  {' '}
                  <Onboarding />{' '}
                </OnboardingProvider>
              }
            />
            <Route path="/auth_flow" element={<AuthCallback />} />
            <Route path="test" element={<CampaignsPage />} />
            <Route path="/auth/accept-invite" element={<AcceptInvite />} />

            {/* Protected dashboard routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AuthorizedRoute>
                    <RegionProvider>
                      <OnboardingProvider>
                        <HeaderStateProvider>
                          <DateRangeProvider>
                            <HeaderStateProvider2>
                              <HeaderStateProvider3>
                                <HeaderAdsProvider>
                                  <DashboardLayout />
                                </HeaderAdsProvider>
                              </HeaderStateProvider3>
                            </HeaderStateProvider2>
                          </DateRangeProvider>
                        </HeaderStateProvider>
                      </OnboardingProvider>
                    </RegionProvider>
                  </AuthorizedRoute>
                </ProtectedRoute>
              }
            >
              {/* Redirect default "/dashboard" path to a default child route */}
              <Route
                path="/dashboard"
                element={<Navigate to="/dashboard/insight-360" replace />}
              />

              {/* Nested routes under "/dashboard" */}
              <Route path="/dashboard">
                {/* <Route index path="insight-360" element={<CampaignsPage />} /> */}
                <Route
                  index
                  path="insight-360"
                  element={
                    <TableFilterProvider>
                      <CampaignIdsProvider>
                        <InsightFiltersProvider>
                          <InsightPage />
                        </InsightFiltersProvider>
                      </CampaignIdsProvider>
                    </TableFilterProvider>
                  }
                />
                {/* <Route path="test2" element={<CampaignsPage />} /> */}
                {/* <Route
                path="growth-tracking"
                element={<MarketingStreamPage />}
              /> */}

                <Route
                  path="growth-tracking"
                  element={
                    <TableFilterProvider>
                      <GrowthTrackingFiltersProvider>
                        <GrowthTracking />
                      </GrowthTrackingFiltersProvider>
                    </TableFilterProvider>
                  }
                />
                {/* <Route path="test2" element={<CampaignsPage />} /> */}
                {/* <Route path="growth-tracking" element={<MarketingStreamPage />} /> */}
                <Route
                  path="snap-suite"
                  element={
                    <TableFilterProvider>
                      <CampaignIdsProvider>
                        <SnapshotPage />
                      </CampaignIdsProvider>
                    </TableFilterProvider>
                  }
                />

                <Route
                  path="campaign-central"
                  element={
                    <DropdownsProvider>
                      <MasterOverviewPage />
                    </DropdownsProvider>
                  }
                />
                <Route
                  path="master-overview"
                  element={<h1>Master Overview</h1>}
                />

                {/* Day-Parting Routes */}
                <Route path="day-parting" element={<DayPartingLayout />}>
                  <Route index element={<DaypartingPage />} />
                  <Route path="scheduler" element={<SchedulerPage />} />
                  <Route path="history" element={<HistoryPage />} />
                  <Route path="log" element={<SchedulerLogPage />} />
                </Route>

                <Route path="sellco-ai" element={<SellcoAI />} />
                <Route path="sellco-ai/:id" element={<SellcoAI />} />
                <Route path="dsp" element={<Dsp />} />
                <Route path="amc">
                  <Route
                    path="audience-instance"
                    element={<AudienceInstancePage />}
                  />
                  <Route path="audience" element={<AudienceReportPage />} />
                  <Route path="insights" element={<AmazonInsights />} />
                  <Route
                    path="create-audience"
                    element={<CreateAudiencePage />}
                  />
                </Route>
                <Route
                  path="blacklist-keywords"
                  element={<BlacklistKeyword />}
                />
                <Route
                  path="asin-categorization"
                  element={<AsinManagement />}
                />
                <Route
                  path="text-categorization"
                  element={<CustomTableComponent />}
                />
                <Route path="accounts" element={<AccountsPage />} />
                <Route path="user-management" element={<UserManagement />} />
                {/* <Route path="accounts" element={<AccountsPage />} />
               <Route path="user-management" element={<UserManagement />} /> */}
                <Route path="profiles" element={<ProfilesPage />} />

                <Route
                  path="automation-rules"
                  element={<Navigate to="automation-rules/budgets" replace />}
                />
                <Route path="automation-rules">
                  <Route index element={<Navigate to="budgets" replace />} />
                  <Route path={''} />
                  <Route path=":ruleType" element={<AutomatedRulesPage />} />
                  <Route
                    path=":ruleType/create"
                    element={<AutomatedRulesPage />}
                  />
                </Route>
                <Route path="ads" element={<AdsPage />}>
                  <Route path="overview" element={<Overview />} />
                  <Route path="overview/:section" element={<Overview />} />
                  <Route path="adsdata" element={<Insights />}>
                    <Route path="" element={<Navigate to="save" replace />} />
                    <Route path="save" element={<div />} />
                    <Route path="visibility" element={<div />} />
                    <Route path="scale" element={<div />} />
                    <Route path="organic-growth" element={<div />} />
                  </Route>
                  <Route path="automation" element={<Automation />}>
                    {/* Default redirect */}
                    <Route index element={<Navigate to="rules" replace />} />

                    {/* Rules page */}
                    <Route path="rules" element={<div />} />

                    {/* Workflows */}
                    <Route path="workflows">
                      <Route index element={<div />} />{' '}
                      {/* default workflows page */}
                      <Route path="budget-dayparting" element={<div />} />
                      <Route path="asin-harvester" element={<div />} />
                      <Route path="keyword-harvester" element={<div />} />
                      
                    </Route>

                    {/* Dayparting tab route */}
                    <Route path="dayparting" element={<div />} />
                  </Route>
                  <Route path="dayparting" element={<Dayparting />} />
                  <Route path="campaigns" element={<CampaignManager />} />
                  <Route
                    path="campaigns/portfolio"
                    element={<PortfolioPage />}
                  />
                  <Route
                    path="manage/adgroups"
                    element={
                      <GrowthTrackingFiltersProvider>
                        <AdGroups />
                      </GrowthTrackingFiltersProvider>
                    }
                  />
                  <Route
                    path="targets-and-search-terms"
                    element={<TargetAndSearchTerms />}
                  />
                  <Route
                    path="marketing-cloud/path-to-conversion"
                    element={<MarketingCloud />}
                  />
                  <Route path="history" element={<History />} />
                  <Route path="stream" element={<Stream />} />
                  <Route path="dsp" element={<Dsp />} />
                </Route>
              </Route>


              {/* Profile routes */}
              <Route path="/dashboard/profile" element={<ProfileLayout />}>
                <Route index element={<ProfileOverviewPage />} />
                <Route path="edit" element={<ProfileEditPage />} />
                <Route
                  path="password"
                  element={<ProfilePasswordChangePage />}
                />
                <Route path="billing" element={<BillingPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route
                  path="refund-and-cancellation"
                  element={<RefundPolicy />}
                />
                <Route path="privacy-policy" element={<PrivacyPolicy />} />
                <Route
                  path="terms-and-services"
                  element={<TermsAndServices />}
                />
              </Route>
            </Route>

            {/* Trial expired — protected (login required) but NOT subject to plan authorization */}
            <Route
              path="/trial-expired"
              element={
                <ProtectedRoute>
                  <TrialExpiredPage />
                </ProtectedRoute>
              }
            />

            {/* Ads revenue over the Scale limit — login required, not plan-gated */}
            <Route
              path="/ad-revenue-limit"
              element={
                <ProtectedRoute>
                  <AdRevenueLimitPage />
                </ProtectedRoute>
              }
            />

            {/* Razorpay returns here after a payment link — login required, not plan-gated */}
            <Route
              path="/payment-complete"
              element={
                <ProtectedRoute>
                  <PaymentCompletePage />
                </ProtectedRoute>
              }
            />

            {/* Unauthorized access route */}
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/no-access" element={<NoAccess />} />
            <Route path="/suspended" element={<SuspendedPage />} />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </>
  );
};
export default AppRoutes;
